import { FunctionComponent, useEffect, useState } from 'react'
import styles from '@/container/daycare/daycareProfile/newsSettings/newsSettings.module.scss'
import Title from '@/components/_custom/Title/title.tsx'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import { useTranslation } from 'react-i18next'
import { PostCategory } from '@/types/NewsFeed.ts'
import { GET_POST_CATEGORIES } from '@/modules/API/news/news.ts'
import Loading from '@/components/Loading/Loading.tsx'
import { Table, TableBody, TableCell, TableRow } from '@mui/material'
import { useLazyQuery } from '@apollo/client'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage.tsx'
import AddPostCategoryDialog from '@/container/daycare/daycareProfile/newsSettings/postCategories/AddPostCategoryDialog.tsx'
import PostCategoryRow from '@/container/daycare/daycareProfile/newsSettings/postCategories/PostCategoryRow.tsx'
import { useDaycareRight } from '@/hooks/useDaycareRight'

interface PostCategoriesProps {
    daycareId: number
}

const PostCategories: FunctionComponent<PostCategoriesProps> = ({ daycareId }: PostCategoriesProps) => {
    const canUpdate: boolean = useDaycareRight('canWriteNewsFeed')

    const { t } = useTranslation()

    const [addPostCategoryDialog, setAddPostCategoryDialog] = useState<boolean>(false)

    const [categories, setCategories] = useState<PostCategory[]>([])

    const [getPostCategories, { loading, data, error, refetch }] = useLazyQuery(GET_POST_CATEGORIES, {
        variables: {
            daycareId: daycareId
        }
    })

    useEffect(() => {
        if (data) {
            setCategories(data.getPostCategories)
        }
    }, [data])

    useEffect(() => {
        getPostCategories()
    }, [daycareId])

    return (
        <div className={styles.BlockPlacement}>
            <div className={styles.Form}>
                <div className={styles.TitleAndButton}>
                    <Title text={t('postCategories')} className={styles.Title} />
                    {canUpdate && (
                        <ButtonTextIcon
                            type={'add'}
                            className={styles.Button}
                            onClick={() => setAddPostCategoryDialog(true)}
                        />
                    )}
                </div>
                {loading && <Loading overall={false} />}
                {error && <ErrorMessage error={t('generalError')} />}
                <Table className={styles.TableComponent}>
                    <TableBody>
                        {categories.length === 0 ? (
                            <TableRow>
                                <TableCell>{t('noPostCategories')}</TableCell>
                            </TableRow>
                        ) : (
                            [...categories]
                                .sort((a: PostCategory, b: PostCategory): number => a.name.localeCompare(b.name))
                                .map((category: PostCategory) => (
                                    <PostCategoryRow
                                        key={category.id}
                                        daycareId={daycareId}
                                        category={category}
                                        canUpdate={canUpdate}
                                        refetch={refetch}
                                    />
                                ))
                        )}
                    </TableBody>
                </Table>
            </div>
            <AddPostCategoryDialog
                open={addPostCategoryDialog}
                handleClose={() => setAddPostCategoryDialog(false)}
                refetch={refetch}
                daycareId={daycareId}
            />
        </div>
    )
}

export default PostCategories
